import type { TooltipRootProps } from '@chakra-ui/react';
import { Portal, Tooltip, useDisclosure } from '@chakra-ui/react';
import React, { useRef } from 'react';
import { useClickAway } from 'react-use';

type MobileSupportTooltipProps = TooltipRootProps & {
  content: React.ReactNode;
  disabled?: boolean;
  tooltipTriggerComponentAs?: React.ElementType;
};

/**
 * A standard tooltip, but it will also work on mobile by clicking on the element, like a popover
 *
 * @param props
 * @returns
 */
function MobileSupportTooltip(props: MobileSupportTooltipProps) {
  const {
    content,
    disabled,
    children,
    tooltipTriggerComponentAs,
    ...tooltipProps
  } = props;
  const { open, onOpen, onToggle, onClose } = useDisclosure();
  const ref = useRef<HTMLButtonElement>(null);
  useClickAway(ref, () => onClose());

  if (disabled) {
    return children;
  }

  return (
    <Tooltip.Root
      open={open}
      interactive
      openDelay={0}
      onOpenChange={(state) => (state.open ? onOpen() : onClose())}
      {...tooltipProps}
    >
      <Tooltip.Trigger
        ref={ref}
        as={tooltipTriggerComponentAs}
        onClick={onToggle}
        onMouseLeave={onClose}
        onMouseEnter={onOpen}
        maxW="fit-content"
      >
        {children}
      </Tooltip.Trigger>
      <Portal>
        <Tooltip.Positioner>
          <Tooltip.Content>{content}</Tooltip.Content>
        </Tooltip.Positioner>
      </Portal>
    </Tooltip.Root>
  );
}

export default MobileSupportTooltip;
